import {HttpClient}                                    from '@angular/common/http';
import {Injectable}                                    from '@angular/core';
import {BehaviorSubject, Observable, of}               from 'rxjs';
import {map, tap}                                      from 'rxjs/operators';
import {query}                                         from 'js-query-builder';
import {BaseService}                                   from './base.service';
import {CreateFormOptionGroupPayload, FormOptionGroup, UpdateFormOptionGroupPayload} from '../models/option-group.model';
import {PaginatedResponseInterface}                    from '@src/app/interfaces/api-paginated-response.interface';

interface ApiResourceResponse<T>
{
    data: T;
}

@Injectable({providedIn: 'root'})
export class FormOptionGroupService extends BaseService
{
    private readonly optionGroupsSubject = new BehaviorSubject<FormOptionGroup[]>([]);
    readonly optionGroups$ = this.optionGroupsSubject.asObservable();

    public constructor(private http: HttpClient)
    {
        super();
    }

    public loadOptionGroups(params?: any): Observable<FormOptionGroup[]>
    {
        const listQuery = this.buildListQuery(params);
        const url = `${this.apiUrl}/${listQuery}`;

        return this.http
            .get<PaginatedResponseInterface<FormOptionGroup>>(url)
            .pipe(
                map((response) => response?.data ?? []),
                tap((groups) => this.optionGroupsSubject.next(groups))
            );
    }

    public refreshOptionGroups(params?: any): Observable<FormOptionGroup[]>
    {
        return this.loadOptionGroups(params);
    }

    public getOptionGroup(id: string, includeOptions = true, forceReload = false): Observable<FormOptionGroup>
    {
        const cached = this.optionGroupsSubject.value.find((group) => group.id === id);
        if (cached && !forceReload && (includeOptions ? cached.options.length > 0 : true)) {
            return of(cached);
        }

        const itemQuery = includeOptions
            ? query(`forms/option-groups/${id}`).param('with', 'options').build()
            : query(`forms/option-groups/${id}`).build();
        const url = `${this.apiUrl}/${itemQuery}`;

        return this.http.get<FormOptionGroup | ApiResourceResponse<FormOptionGroup>>(url).pipe(
            map((response) => this.unwrapResource(response)),
            tap((group) => this.upsertGroup(group))
        );
    }

    public createOptionGroup(payload: CreateFormOptionGroupPayload): Observable<FormOptionGroup>
    {
        const url = `${this.apiUrl}/forms/option-groups`;
        const body = {
            ...payload,
            reusable: payload.reusable ?? true,
        };

        return this.http.post<FormOptionGroup | ApiResourceResponse<FormOptionGroup>>(url, body).pipe(
            map((response) => this.unwrapResource(response)),
            tap((group) => this.upsertGroup(group))
        );
    }

    public updateOptionGroup(id: string, payload: UpdateFormOptionGroupPayload): Observable<FormOptionGroup>
    {
        const url = `${this.apiUrl}/forms/option-groups/${id}`;
        const body = {
            ...payload,
            reusable: payload.reusable ?? true,
        };

        return this.http.put<FormOptionGroup | ApiResourceResponse<FormOptionGroup>>(url, body).pipe(
            map((response) => this.unwrapResource(response)),
            tap((group) => this.upsertGroup(group))
        );
    }

    public deleteOptionGroup(id: string): Observable<void>
    {
        const url = `${this.apiUrl}/forms/option-groups/${id}`;

        return this.http.delete<void>(url).pipe(
            tap(() => this.removeGroup(id))
        );
    }

    private unwrapResource<T>(response: T | ApiResourceResponse<T>): T
    {
        return 'data' in (response as ApiResourceResponse<T>)
            ? (response as ApiResourceResponse<T>).data
            : response as T;
    }

    private upsertGroup(group: FormOptionGroup): void
    {
        const existing = this.optionGroupsSubject.value;
        const index = existing.findIndex((item) => item.id === group.id);
        if (index === -1) {
            this.optionGroupsSubject.next([group, ...existing]);
            return;
        }

        const next = [...existing];
        next[index] = group;
        this.optionGroupsSubject.next(next);
    }

    private removeGroup(id: string): void
    {
        this.optionGroupsSubject.next(this.optionGroupsSubject.value.filter((group) => group.id !== id));
    }

    private buildListQuery(params?: any): string
    {
        const builder = query('forms/option-groups')
            .param('page[size]', '500');

        if (!params) {
            return builder.param('with', 'options').build();
        }

        if (params.scope) {
            builder.filter('scope', params.scope);
        }

        if (typeof params.reusable === 'boolean') {
            builder.filter('reusable', params.reusable ? '1' : '0');
        }

        if (params.tenant) {
            builder.param('tenant', params.tenant);
        }

        if (params.search) {
            builder.param('search', params.search);
        }

        if (params.with !== undefined) {
            if (params.with.length) {
                builder.param('with', params.with);
            }
        } else {
            builder.param('with', 'options');
        }

        return builder.build();
    }
}
